import useResource from "web/hooks/data/useResource"
import { handleMoveChildren } from "web/components/Reorderable/ReorderChildrenHelper"
import { pluralize, removeUnderscore } from "web/services/inflector"
import { CHART_TILE_MOCK_DATA } from "web/components/Tiles/mock"
import { useSWRConfig } from "swr"

export default function useTile({ tileFetchParams, params }) {
  const {
    id,
    routeSuffix,
    overviewKey,
    width,
    parentKeys = [],
  } = tileFetchParams
  const { cache } = useSWRConfig()

  const key =
    routeSuffix && id
      ? `${overviewKey}/tile_${routeSuffix}/${id}?width=${width}`
      : null

  const [tile] = useResource({
    key: key,
    parentKeys: parentKeys,
    params,
  })

  async function handleResizeColumn(column, newWidth, currentTile) {
    const localTile = currentTile.current
    localTile.fields[column.array_index].column_width = newWidth

    await tile.update(
      {
        overview_tile: {
          tile_fields_attributes: [
            {
              id: column.id,
              column_width: newWidth,
            },
          ],
        },
      },
      localTile
    )
  }

  function handleMoveColumn(fromColumn, toColumn) {
    handleMoveChildren({
      prevPos: fromColumn.array_index,
      newPos: toColumn.array_index,
      mainObj: tile,
      movingObjs: tile.fields,
      idName: "tile_field_id",
      firstLayer: "overview_tile",
      secondLayer: "tile_fields_attributes",
    })
  }

  async function handleSortChanged(sortField, sortDesc) {
    const field = tile.fields.find((f) => f.key === sortField)

    await tile.update({
      overview_tile: {
        sort_field_id: field.id,
        sort_direction: sortDesc ? "DESC" : "ASC",
      },
    })
  }

  async function removeGrouping() {
    await tile.update({
      overview_tile: {
        group_field_id: null,
        group_direction: null,
        second_group_field_id: null,
        second_group_direction: null,
      },
    })
  }

  function handleResetTableSettings() {
    tile.update({
      overview_tile: {
        group_field_id: null,
        group_direction: null,
        second_group_field_id: null,
        second_group_direction: null,
        sort_field_id: null,
        sort_direction: null,
      },
    })
  }

  async function setGroupDirection(grouping, newGroupDesc) {
    const groupDirection = newGroupDesc ? "DESC" : "ASC"

    await tile.update({
      overview_tile: {
        [`${grouping}_direction`]: groupDirection,
      },
    })
  }

  async function setGroupField(grouping, newGroupField) {
    const field = tile.fields.find((f) => f.key === newGroupField)

    const update = {
      overview_tile: {
        [`${grouping}_field_id`]: field.id,
        [`${grouping}_direction`]: "ASC",
      },
    }

    // clear second group when setting first group
    if (grouping === "group") {
      update.overview_tile.second_group_field_id = null
    }

    await tile.update(update)
  }

  function getEmptyMessageForTable() {
    const defaultMessage = "There is no data for this table tile."
    if (!tile || !tile.data) return defaultMessage

    const hasData = tile.data.detail.length > 0
    const hasFields = tile.fields.length > 0

    if (!hasData && hasFields) {
      // no assets, has fields
      let message = `There are no ${removeUnderscore(
        pluralize(tile.group_label)
      )} to show.`
      if (tile.conditions.length > 0) {
        message = message + " Your current filters may be hiding some items."
      }
      return message
    } else if (!hasFields && hasData) {
      // no fields, has assets
      return "No fields have been selected for this table."
    } else {
      return defaultMessage
    }
  }

  async function handleGroupChanged(newGroupField) {
    const { group_field_key, second_group_field_key } = tile
    const groupDesc = tile.group_direction === "DESC"
    const secondGroupDesc = tile.second_group_direction === "DESC"

    if (newGroupField === null) {
      await removeGrouping()
      return
    }

    if (group_field_key === newGroupField) {
      await setGroupDirection("group", !groupDesc)
    } else if (second_group_field_key === newGroupField) {
      await setGroupDirection("second_group", !secondGroupDesc, tile)
    } else if (!!second_group_field_key || !group_field_key) {
      await setGroupField("group", newGroupField, tile)
    } else {
      await setGroupField("second_group", newGroupField, tile)
    }
  }

  function fieldToCondition(field) {
    return {
      field_id: field.id,
      operator: "equal",
      type: "View::FieldCondition",
    }
  }

  async function handleAddFieldToConditions(field) {
    await tile.update({
      overview_tile: {
        view_conditions_attributes: [
          {
            ...fieldToCondition(field),
          },
        ],
      },
    })
  }

  async function handleAddCondition(conditionToAdd) {
    await tile.update({
      overview_tile: {
        view_conditions_attributes: [
          {
            field_id: conditionToAdd.field_id,
            match_blank: conditionToAdd.match_blank,
            operator: conditionToAdd.operator,
          },
        ],
      },
    })
  }

  async function handleChangeCondition({ condition, updatedConditionValue }) {
    const valueToSend = Array.isArray(updatedConditionValue.value)
      ? updatedConditionValue.value.map((value) => {
          return value.value
        })
      : updatedConditionValue.value?.value

    const valueUnit = Array.isArray(updatedConditionValue.value)
      ? updatedConditionValue.value[0].unit
      : updatedConditionValue.value?.unit

    await tile.update({
      overview_tile: {
        view_conditions_attributes: [
          {
            id: condition.id,
            ...updatedConditionValue,
            match_blank: updatedConditionValue.match_blank,
            operator: updatedConditionValue.operator,
            value: valueToSend === undefined ? null : valueToSend,
            unit_code: valueUnit === undefined ? null : valueUnit,
          },
        ],
      },
    })
  }

  async function handleDeleteCondition(condition) {
    await tile.update({
      overview_tile: {
        view_conditions_attributes: [
          {
            id: condition.id,
            _destroy: true,
          },
        ],
      },
    })
  }

  async function changeFieldPresentation(fieldId, presentation) {
    await tile.update({
      overview_tile: {
        tile_fields_attributes: [
          {
            id: fieldId,
            presentation,
          },
        ],
      },
    })
  }

  function getDummyChartData() {
    return CHART_TILE_MOCK_DATA
  }

  function getDummyTableData() {
    const data = { detail: [] }

    for (let i = 0; i < 3; i++) {
      data.detail.push({
        id: i,
      })
    }

    const values = {}

    if (tile && tile.fields) {
      tile.fields.forEach((field) => {
        values[field.key] = {
          has_more_info: false,
          percent_value: 25,
          value: {
            ...field.dummy_value,
          },
        }
      })
    }

    data.detail.forEach((detail) => {
      detail.values = values
    })

    data.group_totals = []

    return data
  }

  function getDummyGroupCollection() {
    if (!tile.group_field_id) {
      return []
    }

    const groupFieldDummyValue = tile.fields.find(
      (field) => field.id === tile.group_field_id
    ).dummy_value

    const secondGroupFieldDummyValue =
      tile.second_group_field_id &&
      tile.fields.find((field) => field.id === tile.second_group_field_id)
        ?.dummy_value

    const dummyGroupTitle =
      secondGroupFieldDummyValue === null
        ? groupFieldDummyValue.value
        : `${groupFieldDummyValue.value} / ${secondGroupFieldDummyValue.value}`

    const subjectIds = [0, 1, 2]
    const dummyGroupValues = [{ value: groupFieldDummyValue }]

    if (secondGroupFieldDummyValue) {
      dummyGroupValues.push({ value: secondGroupFieldDummyValue })
    }

    return [
      {
        title: dummyGroupTitle,
        subject_ids: subjectIds,
        group_values: dummyGroupValues,
      },
    ]
  }

  async function handleUpdateTableSettings(data) {
    // always take the first value, we dont need the rest
    const settingChanged = Object.keys(data)[0]

    await tile.update({
      overview_tile: {
        [`${settingChanged}`]: data[settingChanged],
      },
    })
  }

  async function deleteTile() {
    await tile.destroy()
    cache.delete(key)
  }

  return {
    ...tile,
    handleResizeColumn,
    handleMoveColumn,
    handleResetTableSettings,
    handleSortChanged,
    handleGroupChanged,
    getEmptyMessageForTable,
    handleAddFieldToConditions,
    handleAddCondition,
    handleChangeCondition,
    handleDeleteCondition,
    changeFieldPresentation,
    getDummyTableData,
    getDummyGroupCollection,
    getDummyChartData,
    handleUpdateTableSettings,
    deleteTile,
  }
}
